/**
 * Color utility functions for parsing, converting, and validating colors
 * Supports: color names, hex, rgb, hsl, oklch formats with P3 gamut support
 * Uses Culori library for accurate color space conversions
 */

import { 
  rgb, 
  formatHex, 
  oklch, 
  formatCss,
  converter,
  inGamut,
  clampChroma,
  p3
} from 'culori';

// Named colors map (subset of CSS named colors)
const NAMED_COLORS = {
  'red': '#FF0000',
  'green': '#008000',
  'blue': '#0000FF',
  'white': '#FFFFFF',
  'black': '#000000',
  'gray': '#808080',
  'silver': '#C0C0C0',
  'maroon': '#800000',
  'navy': '#000080',
  'teal': '#008080',
  'lime': '#00FF00',
  'aqua': '#00FFFF',
  'yellow': '#FFFF00',
  'fuchsia': '#FF00FF',
  'olive': '#808000',
  'purple': '#800080',
  'orange': '#FFA500',
  'pink': '#FFC0CB',
  'brown': '#A52A2A',
  'cyan': '#00FFFF',
  'magenta': '#FF00FF',
  'transparent': '#00000000'
};

/**
 * Parse OKLCH color with relative chroma support
 * @param {string} input - OKLCH color string
 * @returns {Object|null} Parsed OKLCH color object or null if invalid
 */
function parseOklchWithRelativeChroma(input, workspaceGamut = 'p3') {
  const oklchMatch = input.match(/oklch\(\s*([^)]+)\s*\)/i);
  if (!oklchMatch) return null;
  
  const parts = oklchMatch[1].split(/\s+|\//).map(p => p.trim()).filter(p => p !== '');
  if (parts.length < 3) return null;
  
  const l = parseFloat(parts[0]);
  const cPart = parts[1];
  const h = parseFloat(parts[2]);
  const alpha = parts[3] ? parseFloat(parts[3]) : 1;
  
  if (isNaN(l) || isNaN(h) || isNaN(alpha)) return null;
  
  let c;
  // Check if chroma is a percentage (relative chroma)
  if (cPart.endsWith('%')) {
    const percentage = parseFloat(cPart);
    if (isNaN(percentage)) return null;
    
    // Convert percentage to relative chroma (0-1)
    const relativeChroma = Math.max(0, Math.min(1, percentage / 100));
    
    // Calculate absolute chroma using maximum chroma for this L and H in the **active workspace gamut**
    const maxChroma = getMaxChromaForWorkspace(l, h, alpha, workspaceGamut);
    c = maxChroma * relativeChroma;
    
    // Debug logging for relative chroma conversion
    if (input.includes('--light-tinted-c-bg') || input.includes('75%')) {
      const maxChromaDbg = getMaxChromaForWorkspace(l, h, alpha, workspaceGamut);
      console.log('[parseOklchWithRelativeChroma] L:', l, 'H:', h, 'Percentage:', percentage, 'MaxChroma:', maxChromaDbg, 'Calculated C:', c);
    }
  } else {
    c = parseFloat(cPart);
    if (isNaN(c)) return null;
  }
  
  return {
    mode: 'oklch',
    l: l,
    c: c,
    h: h,
    alpha: alpha
  };
}

/**
 * Workspace gamut: `'p3'` = clamp to Display P3 (preserve L/H); `'srgb'` = clamp to sRGB via OKLCH chroma reduction (preserve L/H).
 * @typedef {'srgb' | 'p3'} WorkspaceGamut
 */

/**
 * Parse color input and return standardized color object
 * @param {string} input - Color string in any supported format
 * @param {WorkspaceGamut} [workspaceGamut='p3'] - App workspace: drives gamut clamp and relative-chroma max in OKLCH inputs
 * @returns {Object} Color object with hex, rgb, hsl, oklch, isValid, isP3, etc.
 */
export function parseColor(input, workspaceGamut = 'p3') {
  if (!input || typeof input !== 'string') {
    return createInvalidColor();
  }

  const gamut = workspaceGamut === 'srgb' ? 'srgb' : 'p3';

  const trimmedInput = input.trim();
  const originalInput = trimmedInput; // Preserve original format
  
  // Extract original hue from OKLCH input to preserve it for extreme lightness values
  let originalHue = null;
  const oklchMatch = trimmedInput.match(/oklch\(\s*([^)]+)\s*\)/i);
  if (oklchMatch) {
    const parts = oklchMatch[1].split(/\s+|\//).map(p => p.trim());
    if (parts.length >= 3) {
      const hueValue = parseFloat(parts[2]);
      if (!isNaN(hueValue)) {
        originalHue = hueValue;
      }
    }
  }

  // Try parsing with Culori first
  let culoriColor;
  try {
    // Handle named colors
    if (NAMED_COLORS[trimmedInput.toLowerCase()]) {
      culoriColor = rgb(NAMED_COLORS[trimmedInput.toLowerCase()]);
    } else {
      // First try parsing OKLCH with relative chroma support
      const relativeChromaColor = parseOklchWithRelativeChroma(trimmedInput, gamut);
      if (relativeChromaColor) {
        culoriColor = relativeChromaColor;
      } else {
        // Try parsing with Culori using the rgb converter which can handle multiple formats
        culoriColor = rgb(trimmedInput);
        // If rgb() didn't work, try oklch() directly
        if (!culoriColor) {
          culoriColor = oklch(trimmedInput);
        }
      }
    }
  } catch (e) {
    console.warn('Color parsing failed:', e);
  }

  if (!culoriColor) {
    return createInvalidColor();
  }

  // Convert to all needed formats using the original color
  const rgbColor = rgb(culoriColor);
  let oklchColor = oklch(culoriColor);
  const hslColor = converter('hsl')(culoriColor);
  
  // Preserve original hue for extreme lightness values where Culori might lose it
  if (originalHue !== null && oklchColor) {
    const lightness = oklchColor.l;
    // For very light (near white) or very dark (near black) colors, preserve the original hue
    if (lightness >= 0.98 || lightness <= 0.02) {

      oklchColor = {
        ...oklchColor,
        h: originalHue
      };
    }
  }
  
  const isInP3 = inGamut('p3')(oklchColor);
  const isInSRGB = inGamut('rgb')(oklchColor);

  // Clamp to workspace gamut while preserving lightness and hue (chroma reduction in OKLCH)
  let displayColor = oklchColor;
  if (gamut === 'srgb') {
    if (!isInSRGB) {
      const clamped = clampChroma({ ...oklchColor, mode: 'oklch' }, 'oklch', 'rgb');
      displayColor = clamped && clamped.mode === 'oklch' ? clamped : oklchColor;
    }
  } else if (!isInP3) {
    displayColor = clampToP3PreservingLH(oklchColor);
  }
  
  // Check if the final color is P3-only (P3 but not sRGB)
  const finalIsInP3 = inGamut('p3')(displayColor);
  const finalIsInSRGB = inGamut('rgb')(displayColor);
  const isP3Only = finalIsInP3 && !finalIsInSRGB;
  
  // Use the clamped color for all RGB-based calculations
  const finalRgbColor = rgb(displayColor);
  const finalHslColor = converter('hsl')(displayColor);
  
  const result = {
    hex: formatHexWithAlpha(finalRgbColor),
    rgb: finalRgbColor,
    hsl: finalHslColor,
    oklch: displayColor, // Use the clamped OKLCH color
    displayColor: displayColor,
    // Prefer alpha from the working color model (displayColor) and fall back to RGB alpha
    alpha: (displayColor && displayColor.alpha != null) ? displayColor.alpha : (finalRgbColor.alpha ?? 1),
    isValid: true,
    isP3: isP3Only,
    isSRGB: finalIsInSRGB,
    originalInput: originalInput,
    contrastColor: getContrastColor(finalRgbColor)
  };

  return result;
}

/**
 * Format color as hex with alpha support (8-digit hex for transparency)
 * @param {Object} color - Color object from Culori
 * @returns {string} Hex string with alpha if needed
 */
function formatHexWithAlpha(color) {
  const rgbColor = rgb(color);
  if (!rgbColor) return '#000000';
  
  const { r, g, b, alpha = 1 } = rgbColor;
  
  // Convert to 0-255 range and clamp to valid range
  const r255 = Math.max(0, Math.min(255, Math.round(r * 255)));
  const g255 = Math.max(0, Math.min(255, Math.round(g * 255)));
  const b255 = Math.max(0, Math.min(255, Math.round(b * 255)));
  const a255 = Math.max(0, Math.min(255, Math.round(alpha * 255)));
  
  // Convert to hex with proper padding
  const rHex = r255.toString(16).padStart(2, '0').toUpperCase();
  const gHex = g255.toString(16).padStart(2, '0').toUpperCase();
  const bHex = b255.toString(16).padStart(2, '0').toUpperCase();
  const aHex = a255.toString(16).padStart(2, '0').toUpperCase();
  
  // Return 8-digit hex if alpha < 1, otherwise 6-digit hex
  if (alpha < 1) {
    return `#${rHex}${gHex}${bHex}${aHex}`;
  } else {
    return `#${rHex}${gHex}${bHex}`;
  }
}

/**
 * Clamp OKLCH color to P3 gamut while preserving lightness and hue
 * Only reduces chroma to find the closest P3 color
 * @param {Object} oklchColor - Original OKLCH color
 * @returns {Object} P3-clamped OKLCH color with same L and H
 */
function clampToP3PreservingLH(oklchColor) {
  const { l, c, h, alpha = 1 } = oklchColor;
  
  // Binary search to find maximum chroma for this L and H in P3
  let minC = 0;
  let maxC = c;
  let bestC = 0;
  const tolerance = 0.001;
  const maxIterations = 50;
  let iterations = 0;
  
  while (maxC - minC > tolerance && iterations < maxIterations) {
    const midC = (minC + maxC) / 2;
    const testColor = {
      mode: 'oklch',
      l: l,
      c: midC,
      h: h,
      alpha: alpha
    };
    
    if (inGamut('p3')(testColor)) {
      bestC = midC;
      minC = midC;
    } else {
      maxC = midC;
    }
    iterations++;
  }
  
  return {
    mode: 'oklch',
    l: l,           // Preserve lightness exactly
    c: bestC,       // Only adjust chroma
    h: h,           // Preserve hue exactly
    alpha: alpha
  };
}

/**
 * Get contrasting text color (black or white)
 */
export function getContrastColor(rgbColor, backgroundRgb = { r: 0, g: 0, b: 0 }) {
  const { r, g, b, alpha = 1 } = rgbColor;
  
  // Helper: convert sRGB channel to linear
  const toLinear = (u) => (u <= 0.03928 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4));
  
  // Assume a dark background behind transparent swatches (checkerboard + app dark theme)
  // If alpha < 1, blend the color with the background in linear light before luminance
  // Background in sRGB 0..1 range
  const bgR = backgroundRgb.r;
  const bgG = backgroundRgb.g;
  const bgB = backgroundRgb.b;
  
  const linR = toLinear(r);
  const linG = toLinear(g);
  const linB = toLinear(b);
  const linBgR = toLinear(bgR);
  const linBgG = toLinear(bgG);
  const linBgB = toLinear(bgB);
  
  const compR = linR * alpha + linBgR * (1 - alpha);
  const compG = linG * alpha + linBgG * (1 - alpha);
  const compB = linB * alpha + linBgB * (1 - alpha);
  
  // Calculate relative luminance using proper weights on the composed color
  const luminance = 0.2126 * compR + 0.7152 * compG + 0.0722 * compB;
  
  // Use WCAG contrast threshold to choose text: light text on dark backgrounds
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}

/**
 * Format OKLCH for display using Culori
 */
export function formatOklch(oklchColor) {
  if (!oklchColor) return 'oklch(0 0 0)';
  
  const { l, c, h, alpha = 1 } = oklchColor;
  
  // Format OKLCH manually to avoid Culori's automatic conversions
  const L = Math.round(l * 1000) / 1000;
  const C = Math.round(c * 1000) / 1000;
  const H = Math.round(h || 0); // Hue is already in degrees from Culori
  const A = alpha !== undefined ? Math.round(alpha * 1000) / 1000 : 1;
  
  if (A < 1) {
    return `oklch(${L} ${C} ${H} / ${A})`;
  }
  return `oklch(${L} ${C} ${H})`;
}

/**
 * Generate CSS color function for P3 with sRGB fallback
 * @param {Object} color - Parsed color object
 * @param {'srgb' | 'p3'} workspaceColorSpace - App workspace toggle: in `'p3'`,
 *   always emit `color(display-p3 …)` from {@link getP3RgbFloats} so swatches
 *   match Figma P3 variables and Display P3 hex labels; in `'srgb'`, keep the
 *   legacy behavior (display-p3 CSS only for {@link parseColor}'s `isP3` colors).
 * @returns {string} CSS with P3 support and sRGB fallback
 */
export function generateP3CSS(color, workspaceColorSpace = 'srgb') {
  if (!color || !color.isValid) {
    return '#000000';
  }
  
  const srgbFallback = color.hex;

  // P3 workspace: every swatch uses Display-P3 channels (same basis as Figma
  // variables in a P3-profiled file). Otherwise in-sRGB-only colors would still
  // render as sRGB hex and diverge from the plugin’s `p3` payload.
  if (workspaceColorSpace === 'p3') {
    const p = getP3RgbFloats(color);
    if (p) {
      const alpha = p.alpha ?? color.alpha ?? 1;
      if (alpha < 1) {
        return `color(display-p3 ${p.r.toFixed(4)} ${p.g.toFixed(4)} ${p.b.toFixed(4)} / ${alpha.toFixed(3)})`;
      }
      return `color(display-p3 ${p.r.toFixed(4)} ${p.g.toFixed(4)} ${p.b.toFixed(4)})`;
    }
    return srgbFallback;
  }

  // sRGB workspace: always paint with sRGB-clamped channels (never display-p3 CSS)
  return srgbFallback;
}

/**
 * Get Display-P3 RGB channel floats (in [0, 1]) for a parsed color.
 *
 * Use this for any pipeline that stores a color into a Display P3 surface
 * (e.g. Figma variables in a P3-profiled document). The returned channels are
 * the OKLCH color clamped (chroma-reduced) into P3 gamut, then converted to
 * gamma-encoded Display P3 — i.e. exactly the same bytes a P3 fill would render.
 *
 * Why this and not `color.rgb`: `.rgb`/`.hex` are sRGB-encoded with destructive
 * channel-clamping, so they lose wide-gamut chroma for P3-only OKLCH inputs.
 * The P3 path preserves that chroma at full 8-bit precision (and slightly more
 * for in-sRGB colors — sRGB and P3 share white but P3 channels distribute the
 * sRGB cube differently, so the floats differ even for in-sRGB inputs).
 *
 * @param {Object} parsedColor - A color object returned by `parseColor(...)`.
 * @returns {{ r: number, g: number, b: number, alpha: number } | null}
 */
export function getP3RgbFloats(parsedColor) {
  if (!parsedColor || !parsedColor.isValid) return null;
  // `displayColor` is already chroma-clamped into P3 by parseColor (see lines
  // ~167-175 above), so converting it via culori's `p3` converter yields
  // channels in [0, 1] for any P3-in-gamut input. We still clamp defensively
  // in case of numerical noise at the gamut boundary.
  const source = parsedColor.displayColor || parsedColor.oklch;
  if (!source) return null;
  try {
    const p3Color = p3(source);
    if (!p3Color) return null;
    const clamp01 = (n) => Math.max(0, Math.min(1, n));
    const alpha = (parsedColor.alpha != null)
      ? parsedColor.alpha
      : (p3Color.alpha != null ? p3Color.alpha : 1);
    return {
      r: clamp01(p3Color.r ?? 0),
      g: clamp01(p3Color.g ?? 0),
      b: clamp01(p3Color.b ?? 0),
      alpha: clamp01(alpha)
    };
  } catch (e) {
    return null;
  }
}

/**
 * Format a parsed color as a 6-digit hex string whose bytes are **Display-P3
 * gamma-encoded channels** (0–255 each), not sRGB.
 *
 * This matches how Figma stores variable values in a Display P3–profiled
 * document after import from the app, and matches tools like OkColor that
 * expose “P3 hex”. It differs from {@link parseColor}'s `.hex`, which is always
 * an sRGB clamp.
 *
 * @param {Object} parsedColor - Result of `parseColor(...)`
 * @returns {string | null} `#RRGGBB` upper-case, or null if unavailable
 */
export function formatP3Hex(parsedColor) {
  const p = getP3RgbFloats(parsedColor);
  if (!p) return null;
  const byte = (x) => Math.max(0, Math.min(255, Math.round(x * 255)));
  const h = (n) => byte(n).toString(16).padStart(2, '0').toUpperCase();
  return `#${h(p.r)}${h(p.g)}${h(p.b)}`;
}

/**
 * Create invalid color object
 */
function createInvalidColor() {
  return {
    hex: '#000000',
    rgb: { r: 0, g: 0, b: 0, alpha: 1 },
    hsl: { h: 0, s: 0, l: 0, alpha: 1 },
    oklch: { l: 0, c: 0, h: 0, alpha: 1 },
    displayColor: { mode: 'rgb', r: 0, g: 0, b: 0, alpha: 1 },
    alpha: 1,
    isValid: false,
    isP3: false,
    isSRGB: true,
    originalInput: '',
    contrastColor: '#FFFFFF'
  };
}

/**
 * Validate and suggest closest valid color
 * Clamps to P3 gamut while preserving hue and lightness
 */
export function validateAndClampColor(input, workspaceGamut = 'p3') {
  const color = parseColor(input, workspaceGamut);
  
  if (!color.isValid) {
    // Return a default color if parsing fails
    return parseColor('#808080', workspaceGamut); // Gray
  }

  // The parseColor function already handles P3 clamping
  return color;
}

/**
 * Convert any color to its OKLCH representation for internal use
 * @param {string} input - Color in any format
 * @returns {Object} OKLCH color object
 */
export function toOklch(input) {
  const color = parseColor(input);
  if (!color.isValid) {
    return { l: 0, c: 0, h: 0, alpha: 1 };
  }
  return color.oklch;
}

/**
 * Check if a color input represents the same color as another
 * @param {string} input1 - First color
 * @param {string} input2 - Second color  
 * @returns {boolean} Whether colors are equivalent
 */
export function colorsEqual(input1, input2, workspaceGamut = 'p3') {
  const color1 = parseColor(input1, workspaceGamut);
  const color2 = parseColor(input2, workspaceGamut);
  
  if (!color1.isValid || !color2.isValid) {
    return false;
  }
  
  // Compare OKLCH values with small tolerance
  const oklch1 = color1.oklch;
  const oklch2 = color2.oklch;
  const tolerance = 0.001;
  
  return Math.abs(oklch1.l - oklch2.l) < tolerance &&
         Math.abs(oklch1.c - oklch2.c) < tolerance &&
         Math.abs((oklch1.h || 0) - (oklch2.h || 0)) < tolerance &&
         Math.abs((oklch1.alpha || 1) - (oklch2.alpha || 1)) < tolerance;
}

/**
 * Calculate maximum chroma for given lightness and hue in P3 gamut
 * @param {number} l - Lightness (0-1)
 * @param {number} h - Hue (0-360 degrees)
 * @param {number} alpha - Alpha (0-1)
 * @returns {number} Maximum chroma value for L and H in P3 gamut
 */
export function getMaxChromaInP3(l, h, alpha = 1) {
  // Binary search to find maximum chroma for this L and H in P3
  let minC = 0;
  let maxC = 0.37; // Theoretical maximum chroma in OKLCH
  let bestC = 0;
  const tolerance = 0.001;
  const maxIterations = 50;
  let iterations = 0;
  
  while (maxC - minC > tolerance && iterations < maxIterations) {
    const midC = (minC + maxC) / 2;
    const testColor = {
      mode: 'oklch',
      l: l,
      c: midC,
      h: h,
      alpha: alpha
    };
    
    if (inGamut('p3')(testColor)) {
      bestC = midC;
      minC = midC;
    } else {
      maxC = midC;
    }
    iterations++;
  }
  
  return bestC;
}

/**
 * Maximum chroma at (L, H, alpha) that still lies in the **sRGB** gamut (OKLCH → RGB in gamut).
 * Same L/H preservation strategy as {@link getMaxChromaInP3}.
 */
export function getMaxChromaInSrgb(l, h, alpha = 1) {
  let minC = 0;
  let maxC = 0.37;
  let bestC = 0;
  const tolerance = 0.001;
  const maxIterations = 50;
  let iterations = 0;

  while (maxC - minC > tolerance && iterations < maxIterations) {
    const midC = (minC + maxC) / 2;
    const testColor = {
      mode: 'oklch',
      l,
      c: midC,
      h,
      alpha
    };
    if (inGamut('rgb')(testColor)) {
      bestC = midC;
      minC = midC;
    } else {
      maxC = midC;
    }
    iterations++;
  }
  return bestC;
}

/**
 * @param {WorkspaceGamut} workspaceGamut
 */
export function getMaxChromaForWorkspace(l, h, alpha = 1, workspaceGamut = 'p3') {
  return workspaceGamut === 'srgb' ? getMaxChromaInSrgb(l, h, alpha) : getMaxChromaInP3(l, h, alpha);
}

/**
 * Calculate relative chroma percentage from a reference color
 * @param {Object} referenceOklch - Reference OKLCH color object
 * @param {WorkspaceGamut} [workspaceGamut='p3']
 * @returns {number} Relative chroma as a percentage (0-1)
 */
export function getRelativeChroma(referenceOklch, workspaceGamut = 'p3') {
  const { l, c, h, alpha = 1 } = referenceOklch;
  
  // Get maximum chroma for this lightness and hue in the active workspace gamut
  const maxChroma = getMaxChromaForWorkspace(l, h, alpha, workspaceGamut);
  
  // Avoid division by zero
  if (maxChroma === 0) {
    return 0;
  }
  
  // Calculate relative chroma as percentage
  return c / maxChroma;
}

/**
 * Apply relative chroma to new lightness and hue values
 * @param {number} relativeChroma - Relative chroma percentage (0-1)
 * @param {number} newL - New lightness (0-1)
 * @param {number} newH - New hue (0-360 degrees)
 * @param {number} alpha - Alpha (0-1)
 * @param {WorkspaceGamut} [workspaceGamut='p3']
 * @returns {number} Absolute chroma value for new L and H
 */
export function applyRelativeChroma(relativeChroma, newL, newH, alpha = 1, workspaceGamut = 'p3') {
  const maxChroma = getMaxChromaForWorkspace(newL, newH, alpha, workspaceGamut);
  return maxChroma * relativeChroma;
}
